import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, endpoints, joinUrl } from '../api/config';
import { triggerLogout } from '../hooks/useCachedData';
import { useSessionManager } from '../hooks/useSessionManager';
import { useTabCloseHandler, usePageStateHandler } from '../hooks/useTabCloseHandler';

const AuthContext = createContext(null);
const BASE_URL = import.meta.env.VITE_API_URL;
const ACCESS_TOKEN_EXPIRY = 30 * 60 * 1000; // 30 minutes

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTwoFactorVerified, setIsTwoFactorVerified] = useState(false);
  const [features, setFeatures] = useState(null);
  const navigate = useNavigate();

  // Initialize tab close handler and page state handler
  useTabCloseHandler();
  usePageStateHandler();

  const hydrateFromMasterAdmin = async (username) => {
    try {
      const resp = await api.get(endpoints.admins);
      // Support multiple response shapes: array, {data:[]}, {results:[]}, {records:[]}
      const payload = resp?.data ?? resp;
      const list = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data
          : (Array.isArray(payload?.results) ? payload.results
            : (Array.isArray(payload?.records) ? payload.records : [])));
      const me = (list || []).find((u) => String(u?.username || '').toLowerCase() === String(username || '').toLowerCase());
      if (!me) return;

      // Build features: merge me.features with top-level userPickStatus and PartsDashboard (backend may return either)
      const baseFeatures = (me.features && typeof me.features === 'object') ? { ...me.features } : {};
      const userPickStatus = me.userPickStatus === true || me.user_pick_status === true;
      const partsDashboard = me.PartsDashboard === true || me.parts_dashboard === true;
      const shippingLabelErrors = me.shippingLabelErrors === true || me.shipping_label_errors === true;
      const mergedFeatures = {
        ...baseFeatures,
        userPickStatus: baseFeatures.userPickStatus ?? userPickStatus,
        PartsDashboard: baseFeatures.PartsDashboard ?? partsDashboard,
        shippingLabelErrors: baseFeatures.shippingLabelErrors ?? shippingLabelErrors,
      };
      sessionStorage.setItem('adminFeatures', JSON.stringify(mergedFeatures));
      setFeatures(mergedFeatures);

      // Save filters
      const filters = Array.isArray(me.filters) ? me.filters : [];
      const map = {};
      (filters || []).forEach((f) => {
        try {
          const [title, colId] = String(f.key || '').split(':');
          if (!title || !colId) return;
          if (!Array.isArray(map[title])) map[title] = [];
          map[title].push({ id: colId, value: f.type });
        } catch { }
      });
      const serialized = JSON.stringify(map);
      sessionStorage.setItem('tableFilters', serialized);
      // Also keep a frontend fallback copy
      localStorage.setItem('tableFilters', serialized);
      // Notify tables to re-read filters
      window.dispatchEvent(new CustomEvent('tableFiltersUpdated'));
      sessionStorage.setItem('tableFiltersHydrated', 'true');
    } catch { }
  };

  const login = (newToken, userData, twoFactorVerified = false) => {
    setToken(newToken);
    setUser(userData);
    setIsTwoFactorVerified(twoFactorVerified);

    // Get features from session storage immediately as fallback
    const storedFeatures = sessionStorage.getItem('adminFeatures');
    const adminFeatures = storedFeatures ? JSON.parse(storedFeatures) : null;
    if (adminFeatures) {
      setFeatures(adminFeatures);
    }

    sessionStorage.setItem('token', newToken);
    sessionStorage.setItem('tokenCreationTime', Date.now().toString());
    sessionStorage.setItem('username', userData.username);
    sessionStorage.setItem('twoFactorVerified', twoFactorVerified.toString());

    // Dispatch custom event for username change
    window.dispatchEvent(new CustomEvent('sessionStorageChange', {
      detail: { key: 'username', value: userData.username }
    }));

    // Hydrate features and filters from master admin details (async)
    hydrateFromMasterAdmin(userData.username);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsTwoFactorVerified(false);
    setFeatures(null);
    sessionStorage.clear();

    // Dispatch custom event for username change
    window.dispatchEvent(new CustomEvent('sessionStorageChange', {
      detail: { key: 'username', value: null }
    }));

    triggerLogout(); // Trigger cache invalidation

    // Use window.location.href to force a complete page reload and reset the app state
    window.location.href = '/login';
  };

  // Initialize session management and get SessionRefreshDialog
  const { SessionRefreshDialog } = useSessionManager(token, login);

  // Memoize the fully authenticated calculation to prevent infinite loops
  const isFullyAuthenticated = useMemo(() => {
    const username = sessionStorage.getItem('username');
    const twoFactorVerified = sessionStorage.getItem('twoFactorVerified') === 'true';
    const intermediateCompleted = sessionStorage.getItem('intermediateFeatureCompleted') === 'true';
    const hasToken = sessionStorage.getItem('token');

    // User is fully authenticated if they have a token and:
    // 1. 2FA is verified, OR
    // 2. Intermediate feature is completed (2FA setup is done)
    // Note: ALL admins (both regular and super) must complete 2FA
    // The 2FA status is checked fresh on every login, so cached status is not relied upon
    return !!hasToken && (twoFactorVerified || intermediateCompleted);
  }, [token, isTwoFactorVerified]);

  useEffect(() => {
    // Check for existing token on mount
    const existingToken = sessionStorage.getItem('token');
    const tokenCreationTime = sessionStorage.getItem('tokenCreationTime');
    const existingUsername = sessionStorage.getItem('username');
    const isRefreshing = sessionStorage.getItem('isRefreshing') === 'true';
    const twoFactorVerified = sessionStorage.getItem('twoFactorVerified') === 'true';

    if (existingToken && tokenCreationTime) {
      const now = Date.now();
      const tokenAge = now - parseInt(tokenCreationTime);

      console.log('[AuthContext] Session Check:', {
        tokenAgeMinutes: Math.round(tokenAge / 1000 / 60),
        expiryMinutes: ACCESS_TOKEN_EXPIRY / 1000 / 60,
        isRefreshing
      });

      // If token is still valid (less than 30 minutes old)
      if (tokenAge < ACCESS_TOKEN_EXPIRY) {
        setToken(existingToken);
        setIsTwoFactorVerified(twoFactorVerified);
        // Set user data from session storage
        if (existingUsername) {
          setUser({ username: existingUsername });

          // Immediately restore features from session storage (no waiting)
          const storedFeatures = sessionStorage.getItem('adminFeatures');
          if (storedFeatures) {
            try {
              const adminFeatures = JSON.parse(storedFeatures);
              setFeatures(adminFeatures);
            } catch (e) {
              console.error('Failed to parse stored features:', e);
            }
          }

          // Dispatch custom event for username change
          window.dispatchEvent(new CustomEvent('sessionStorageChange', {
            detail: { key: 'username', value: existingUsername }
          }));

          // Attempt to hydrate fresh features/filters from backend if available (async, no blocking)
          hydrateFromMasterAdmin(existingUsername);

          // If no filters in session yet, hydrate from master admin
          if (!sessionStorage.getItem('tableFilters')) {
            hydrateFromMasterAdmin(existingUsername);
          }
        }
      } else if (!isRefreshing) {
        // Only clear token if it's not a refresh and token is expired
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('tokenCreationTime');
        sessionStorage.removeItem('username');
        sessionStorage.removeItem('twoFactorVerified');
        sessionStorage.removeItem('adminFeatures');
        setToken(null);
        setUser(null);
        setIsTwoFactorVerified(false);
        setFeatures(null);
      }
    } else {
      // If no token exists, ensure we're logged out
      setToken(null);
      setUser(null);
      setIsTwoFactorVerified(false);
      setFeatures(null);
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tokenCreationTime');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('twoFactorVerified');
      sessionStorage.removeItem('adminFeatures');
    }

    setIsLoading(false);
  }, []);

  if (isLoading) {
    return null; // or a loading spinner
  }

  return (
    <AuthContext.Provider value={{
      token,
      user,
      login,
      logout,
      isAuthenticated: !!token,
      isTwoFactorVerified,
      isFullyAuthenticated,
      features,
      SessionRefreshDialog
    }}>
      {children}
      <SessionRefreshDialog />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 